/* eslint-disable */

/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path ui
 * @description Navigation main menu
 * @section 4.5
 *
 */
import { Typography } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import clsx from "clsx";
import { getElementName } from "qpr-base/tplutils/utils";
import React, { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Data } from "./../apihooks/useNavitems";
import { useAppContext } from "./../context/AppCtxProvider";
import useCurrentUser from "./../context/UserContext";
import useLink from "./../hooks/useLink";
import { checkIfExternal, getFileApiURL } from "./../utils/url-utils";
import { makeThemedStyles } from "./../viewcomponents/utils";
import VerticalScroll from "./VerticalScroll";
import useAdminRights from "../../backend/hooks/misc/useAdminRights";
import { filterNavItemsForUser } from "../../utils/navItemRoleFilters";
import { getNavItemDisplayLabel, getNavItemIconPath, getPublicNavItemIconUrl, resolveNavItemIconPath } from "../../utils/navItemDisplayLabels";
import adminPanelIcon from "../../assets/navitems/AdminPanel.svg";
import adminPanelSettingsIcon from "../../assets/navitems/AdminPanelSettings.svg";
import adminToolsIcon from "../../assets/navitems/AdminTools.svg";
import cascadeIcon from "../../assets/navitems/cascade.svg";
import corporateIcon from "../../assets/navitems/corporate.svg";
import dubaiPlanIcon from "../../assets/navitems/DubaiPlan.svg";
import entityIcon from "../../assets/navitems/Entity.svg";
import executiveDashboardIcon from "../../assets/navitems/ExecutiveDashboard.svg";
import executiveDashboardsIcon from "../../assets/navitems/ExecutiveDashboards.svg";
import executiveIcon from "../../assets/navitems/executive.svg";
import inputValueIcon from "../../assets/navitems/InputValue.svg";
import iconMyDashboards from "../../assets/navitems/icon_my_dashboards.svg";
import kpisAndProgramsManagementIcon from "../../assets/navitems/KPIsAndProgramsManagement.svg";
import kpisAndProjectsManagementIcon from "../../assets/navitems/KPIsAndProjectsManagement.svg";
import manageWorkflowsIcon from "../../assets/navitems/ManageWorkflows.svg";
import myTasksIcon from "../../assets/navitems/MyTasks.svg";
import operationDashboardIcon from "../../assets/navitems/OperationDashboard.svg";
import overviewIcon from "../../assets/navitems/Overview.svg";
import projectsIcon from "../../assets/navitems/Projects.svg";
import reportsIcon from "../../assets/navitems/Reports.svg";
import reportingIcon from "../../assets/navitems/reporting.svg";
import sectorsIcon from "../../assets/navitems/Sectors.svg";
import strategicKpiIcon from "../../assets/navitems/StrategicKpi.svg";
import workflowsIcon from "../../assets/navitems/workflows.svg";
import workflowAdminIcon from "../../assets/navitems/WorkflowAdmin.svg";

const NAVITEM_ICON_MAP: Record<string, string> = {
  "AdminPanel.svg": adminPanelIcon,
  "AdminPanelSettings.svg": adminPanelSettingsIcon,
  "AdminTools.svg": adminToolsIcon,
  "cascade.svg": cascadeIcon,
  "corporate.svg": corporateIcon,
  "DubaiPlan.svg": dubaiPlanIcon,
  "Entity.svg": entityIcon,
  "ExecutiveDashboard.svg": executiveDashboardIcon,
  "ExecutiveDashboards.svg": executiveDashboardsIcon,
  "executive.svg": executiveIcon,
  "InputValue.svg": inputValueIcon,
  "icon_my_dashboards.svg": iconMyDashboards,
  "KPIsAndProgramsManagement.svg": kpisAndProgramsManagementIcon,
  "KPIsAndProjectsManagement.svg": kpisAndProjectsManagementIcon,
  "ManageWorkflows.svg": manageWorkflowsIcon,
  "MyTasks.svg": myTasksIcon,
  "OperationDashboard.svg": operationDashboardIcon,
  "Overview.svg": overviewIcon,
  "Projects.svg": projectsIcon,
  "Reports.svg": reportsIcon,
  "reporting.svg": reportingIcon,
  "Sectors.svg": sectorsIcon,
  "StrategicKpi.svg": strategicKpiIcon,
  "workflows.svg": workflowsIcon,
  "WorkflowAdmin.svg": workflowAdminIcon,
};

const getNavitemFileName = (iconPath: string): string | null => {
  if (!iconPath) return null;
  if (iconPath.includes("/api/files")) {
    const filePart = iconPath.split("file=")[1];
    if (!filePart) return null;
    const decoded = decodeURIComponent(filePart).split("&")[0];
    return decoded.split("/").pop() || null;
  }
  const fileName = iconPath.split("/").pop();
  return fileName ? fileName.split("?")[0] : null;
};

const resolveNavitemIcon = (iconPath: string): string | null => {
  const fileName = getNavitemFileName(iconPath);
  if (!fileName) return null;
  return NAVITEM_ICON_MAP[fileName] || null;
};
interface MenuColumnProps {
  data?: Data;
  level: number;
  tenant: string;
  values: Array<string | boolean>;
  handleChange: (newValue: string | boolean, level: number) => void;
  toggleMenu: () => void;
  onSelect?: (item: Data) => void;
  RenderTab?: React.FC<{
    item: Data;
    onClick: () => void;
  }>;
}
export const useStyles = makeThemedStyles(
  "HorizontalMenu.MenuColumn",
  (theme) => ({
    menuColumn: {
      paddingLeft: theme.spacing(6),
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.background.paper,
      height: "100%",
      "& $button": {
        visibility: "hidden",
      },
      [theme.breakpoints.down("sm")]: {
        "& $button": {
          visibility: "hidden",
        },
        "&$menuColumn:not(:first-of-type)": {
          "& $button": {
            visibility: "visible",
          },
          position: "absolute",
          left: "0px",
        },
      },
    },
    button: {},
    selectedTab: {
      borderRight: "1px solid rgb(0, 169, 163)",
      "& *": {
        color: "rgb(0, 169, 163) !important",
      },
    },
    column_header: {
      padding: `0 ${theme.spacing(2)}px`,
      color: "rgb(17, 94, 103)",
      margin: "1.64em 0 0.071em 0",
      fontSize: "1.6em !important",
      lineHeight: "2em",
      letterSpacing: "0.05px",
      textAlign: "left",
    },
    navItem: {
      fontSize: "0.8em !important",
      letterSpacing: "0.05px",
      margin: 0,
      width: "100%",
      textAlign: "left",
    },
    divider: {
      float: "left",
      marginLeft: "-1px",
      zIndex: 1,
    },
    tabRoot: {
      textTransform: "none",
      padding: 0,
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      maxWidth: "1000px",
    },
    tab: {
      paddingTop: "0.55em",
      paddingBottom: "0.55em",
      "&.not-visited": {
        "& h6": {
          color: "rgb(17, 94, 103) !important",
        },
      },
      "& h6": {
        color: "rgb(109, 132, 136)",
      },
    },
    activeTab: {
      borderRight: "1px solid rgb(0, 169, 163)",
      "& h6": {
        color: "rgb(0, 169, 163) !important",
      },
    },
    childNavItem: {
      "& h6": {
        fontSize: "0.65em !important",
        color: "rgb(109, 132, 136)",
      },
    },
    wrapper: {
      flexDirection: "row-reverse",
      justifyContent: "start",
    },
    labelIcon: {
      minHeight: "0px",
    },
    icon: {
      marginRight: "1.065em",
      marginLeft: "0.66em",
    },
    iconImg: {
      height: "1.6em",
      maxWidth: "1.6em",
      order: 2,
      marginBottom: "0px!important",
    },
    tabsRoot: {},
    selectedIndicator: { display: "none" },
    scrollContainer: {
      flex: "initial",
      maxHeight: "453px",
    },
    contentContainer: {
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none !important",
      },
    },
    missingImage: {
      display: "none",
    },
    column: {
      minWidth: "calc(100% / 6)",
      maxWidth: "calc(100% / 5)",
      animation: `$fadeIn 1s ${theme.transitions.easing.easeInOut}`,
    },
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 1,
      },
    },
  })
);
// move the get icon from the render loop
export const getIcon = (item: Data) => {
  const [hasError, setError] = React.useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const iconPath = resolveNavItemIconPath(item);
  const resolveIconUrl = (path: string) => {
    // Admin Panel (and other overrides): prefer webpack-bundled / public asset, never Mongo.
    const publicUrl = getPublicNavItemIconUrl(item);
    if (publicUrl) {
      const localIcon = resolveNavitemIcon(path) || resolveNavitemIcon(publicUrl);
      return localIcon || publicUrl;
    }
    const localIcon = resolveNavitemIcon(path);
    if (localIcon) return localIcon;
    return getFileApiURL(path);
  };
  // in case no fallback and error render DIV with classname "missingImage" which can be themed
  if (hasError || !iconPath) {
    return <div className={classes.missingImage} />;
  }
  return typeof item.icon === "string" || getNavItemIconPath(item) ? (
    <div
      style={{
        height: "2em",
        width: "2em",
        marginRight: "1.065em",
        marginLeft: "0.66em",
        background: "rgb(0, 169, 163)",
        WebkitMask: `url(${resolveIconUrl(iconPath)}) no-repeat center / contain`,
      }}
      className={clsx(classes.iconImg)}
    />
  ) : item.icon ? (
    item.icon
  ) : (
    <div className={classes.missingImage} />
  );
};
const MenuColumn = ({
  data,
  level,
  tenant,
  toggleMenu,
  handleChange,
  RenderTab,
  values = [],
}: MenuColumnProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const ctx = useAppContext();
  const { language, rtl, model } = ctx;
  const user = useCurrentUser();
  const isAdmin = useAdminRights();
  const link = useLink();
  // Check if user has tenantAdmin flag (must be explicitly true)
  // Only check if profile exists and tenantAdmin is explicitly true
  const isTenantAdmin = Boolean(user?.profile?.tenantAdmin === true);
  // User is considered admin if they have tenantAdmin flag OR admin rights
  // Only show admin items if user explicitly has admin access
  // Be strict: both checks must be explicit
  const hasAdminAccess = isTenantAdmin || Boolean(isAdmin);
  const navRef = useRef<HTMLSpanElement[]>([]);
  const dividerRef = useRef<HTMLHRElement>(null);
  const mouseThrottleTimer = useRef<NodeJS.Timeout>();
  interface IData {
    [key: string]: any;
  }
  const temp: IData = {};
  if (!data) return null;
  
  const filteredData = {
    ...data,
    children: filterNavItemsForUser(data.children, {
      profile: user?.profile,
      meaGroups:
        (user?.profile as { meaGroups?: string[] } | undefined)?.meaGroups ||
        (Array.isArray((user as any)?.groups) ? (user as any).groups : null),
      hasAdminAccess,
    }),
  };
  
  const getTitle = (item: Data) => {
    if (item.customValue1) {
      return getElementName(
        { ...item, ...{ JS_ATTR: item.customValue1 } },
        language
      );
    }
    return getNavItemDisplayLabel(item);
  };
  const groupedData = filteredData.children.reduce((obj, item) => {
    // If the length doesn't already exist as a key in the object, create it
    // eslint-disable-next-line no-prototype-builtins
    if (item.group && !obj.hasOwnProperty(item.group)) {
      obj[item.group as string] = [];
      // eslint-disable-next-line no-prototype-builtins
    } else if (!item.group && !obj.hasOwnProperty("nogroup")) {
      obj.nogroup = [];
    }
    // Push the number to its integer key
    // eslint-disable-next-line no-prototype-builtins
    if (item.group && obj.hasOwnProperty(item.group))
      obj[item.group as string].push(item);
    else obj.nogroup.push(item);
    // Pass the object on to the next loop
    return obj;
  }, temp);
  function addToRefs(el: HTMLSpanElement | null) {
    if (
      el &&
      el.parentElement?.parentElement &&
      Array.isArray(navRef.current) &&
      !navRef.current.includes(el)
    ) {
      navRef.current.push(el.parentElement.parentElement);
    }
  }
  useEffect(() => {
    function handleOver(target: HTMLSpanElement) {
      navRef.current.forEach((nav) => {
        nav.classList.remove("not-visited");
        if (target === nav) {
          nav.classList.add(classes.activeTab);
        } else {
          nav.classList.remove(classes.activeTab);
        }
      });
    }
    function handleOut() {
      navRef.current.forEach((nav) => {
        nav.classList.remove(classes.activeTab);
      });
    }
    if (Array.isArray(navRef.current)) {
      navRef.current.forEach((nav) => {
        if (level === 0) nav.classList.add("not-visited");
        nav.addEventListener("mouseover", () => handleOver(nav));
        nav.addEventListener("mouseout", handleOut);
      });
    }
    return () => {
      if (Array.isArray(navRef.current)) {
        navRef.current.forEach((nav) => {
          nav.removeEventListener("mouseover", () => handleOver(nav));
          nav.removeEventListener("mouseout", handleOut);
        });
      }
    };
  }, []);
  function handleMouseEnter(_id: string, level: number) {
    mouseThrottleTimer.current = setTimeout(() => {
      handleChange(_id, level);
      if (mouseThrottleTimer.current) clearTimeout(mouseThrottleTimer.current);
    }, 300);
  }
  function handleMouseLeave() {
    if (mouseThrottleTimer.current) clearTimeout(mouseThrottleTimer.current);
  }

  // Helper function to parse link and extract page name
  // Links can be: /cascade, /strategymap, /${model}/${page}, /@metricsModelId/page, etc.
  const parseLink = (link: string): string | null => {
    if (!link || link === "/") return null;
    // Remove leading slash and split by /
    const parts = link.replace(/^\/+/, "").split("/");
    // Remove any query parameters
    const firstPart = parts[0]?.split("?")[0];
    
    // Handle @metricsModelId placeholder or actual model IDs
    if (firstPart === "@metricsModelId" || (firstPart && /^\d+$/.test(firstPart))) {
      // Get the next part as the page name
      if (parts.length > 1) {
        return parts[1]?.split("?")[0] || null;
      }
      return null;
    }
    
    // Otherwise, return the first part as the page name
    return firstPart || null;
  };

  // Handle navigation with useLink to preserve model and period
  const handleNavigation = (item: Data, e: React.MouseEvent) => {
    e.preventDefault();
    toggleMenu();
    if (checkIfExternal(item.link)) {
      window.open(item.link, "_blank");
      return;
    }
    const pageName = parseLink(item.link);
    if (pageName) {
      // Ensure we use the default 2026 model ID if current model is 2025
      // This ensures navigation always goes to 2026 unless explicitly set to 2025
      // 2025 model id is 1954172446 (827938453 is legacy/old)
      const targetModel =
        ctx.modelId === "1954172446" || ctx.modelId === "827938453"
          ? "2106935752"
          : ctx.modelId;
      // Use useLink to navigate while preserving period but ensuring 2026 model
      link({ page: pageName, model: targetModel });
    } else {
      // Fallback: process the link to replace @metricsModelId with 2026 model
      let processedLink = item.link;
      if (processedLink.includes("@metricsModelId")) {
        const defaultModel =
          ctx.modelId === "1954172446" || ctx.modelId === "827938453"
            ? "2106935752"
            : ctx.modelId || "2106935752";
        processedLink = processedLink.replace(/@metricsModelId/g, defaultModel);
      }
      // If link already has a model ID, replace 2025 with 2026
      const linkParts = processedLink.split("/").filter(Boolean);
      if (linkParts.length > 0 && /^\d+$/.test(linkParts[0])) {
        if (linkParts[0] === "1954172446" || linkParts[0] === "827938453") {
          linkParts[0] = "2106935752";
          processedLink = `/${linkParts.join("/")}`;
        }
      }
      window.location.href = `/${tenant}${processedLink}`;
    }
  };
  return (
    <>
      <div data-testid="sideways-megamenu-column" className={classes.column}>
        {Object.entries(groupedData).map(
          (entry: [string, Data[]], index: number) => {
            return (
              <Fragment key={`${entry[0]}`}>
                <Typography
                  key={entry[0]}
                  variant="subtitle2"
                  className={classes.column_header}
                  style={{
                    visibility: entry[0] !== "nogroup" ? "visible" : "hidden",
                    cursor:
                      entry[0] !== "nogroup" && index !== 0
                        ? "pointer"
                        : "default",
                    textAlign: rtl ? "right" : "left",
                  }}
                  align="right"
                  onClick={
                    index !== 0
                      ? () => handleChange(false, level - 1)
                      : () => {}
                  }
                >
                  {entry[0] !== "nogroup" ? t(entry[0]) : "."}
                </Typography>
                <VerticalScroll
                  containerClassName={classes.scrollContainer}
                  contentClassName={classes.contentContainer}
                >
                  <Tabs
                    orientation="vertical"
                    value={
                      entry[1].some((item) => item._id === values[level])
                        ? values[level]
                        : false
                    }
                    aria-label="Vertical megamenu"
                    classes={{
                      root: classes.tabsRoot,
                      indicator: classes.selectedIndicator,
                    }}
                  >
                    {entry[1].map((item) => {
                      if (item.children.length > 0)
                        return (
                          <Tab
                            label={
                              <>
                                <Typography
                                  variant="subtitle2"
                                  ref={addToRefs}
                                  className={classes.navItem}
                                  style={{ textAlign: rtl ? "right" : "left" }}
                                >
                                  {getTitle(item)}
                                </Typography>
                              </>
                            }
                            icon={getIcon(item)}
                            key={item._id}
                            value={item._id}
                            onClick={(e) => handleNavigation(item, e)}
                            disabled={!item.enabled}
                            data-testid="sideways-megamenu-tab"
                            className={clsx(
                              classes.tab,
                              level !== 0 && classes.childNavItem
                            )}
                            onMouseEnter={() =>
                              handleMouseEnter(item._id, level)
                            }
                            onMouseLeave={() => handleMouseLeave()}
                            classes={{
                              selected: classes.selectedTab,
                              root: classes.tabRoot,
                              wrapper: classes.wrapper,
                              labelIcon: classes.labelIcon,
                            }}
                          />
                        );
                      if (RenderTab) {
                        return <RenderTab item={item} onClick={toggleMenu} />;
                      }
                      return (
                        <Tab
                          disabled={!item.enabled}
                          onClick={(e) => handleNavigation(item, e)}
                          key={item._id}
                          data-testid="sideways-megamenu-tab"
                          className={clsx(
                            classes.tab,
                            level !== 0 && classes.childNavItem
                          )}
                          label={
                            <>
                              <Typography
                                variant="subtitle2"
                                ref={addToRefs}
                                className={classes.navItem}
                                style={{ textAlign: rtl ? "right" : "left" }}
                              >
                                {getTitle(item)}
                              </Typography>
                            </>
                          }
                          icon={getIcon(item)}
                          value={item._id}
                          classes={{
                            root: classes.tabRoot,
                            wrapper: classes.wrapper,
                            labelIcon: classes.labelIcon,
                          }}
                        />
                      );
                    })}
                  </Tabs>
                </VerticalScroll>
                {index + 1 < Object.entries(groupedData).length && <Divider />}
              </Fragment>
            );
          }
        )}
      </div>
      <Divider
        ref={dividerRef}
        orientation="vertical"
        flexItem
        className={classes.divider}
      />
      {filteredData.children
        .filter((item) => values[level] === item._id)
        .map((item) => {
          if (item.children.length > 0)
            return (
              <MenuColumn
                key={item._id}
                data={item}
                level={level + 1}
                tenant={tenant}
                handleChange={handleChange}
                RenderTab={RenderTab}
                values={values}
                toggleMenu={toggleMenu}
              />
            );
          return null;
        })}
    </>
  );
};
export default MenuColumn;
